import React, { useEffect, useRef, useState } from 'react';
import styles from 'components/fullscreenPlayer/fullscreenPlayer.module.css';
import ReactPlayer from 'react-player';
import { PlayIconRed } from 'components/vectors/PlayIconRed';
import { useVideoPlayer } from 'components/fullscreenPlayer/videoContext';

interface IFullscreenPlayer {
    videoUrl?: string;
    imageUrl?: string;
    playerId?: string;
    handleImageLoad?: () => void;
    isImgLoaded?: boolean;
    title?: string;
}

interface CustomHTMLVideoElement extends HTMLVideoElement {
    webkitRequestFullscreen?: () => void;
    msRequestFullscreen?: () => void;
}

const FullscreenPlayer = ({
    videoUrl,
    imageUrl,
    playerId,
    handleImageLoad,
    isImgLoaded,
    title,
}: IFullscreenPlayer) => {
    const playerRef = useRef<ReactPlayer>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    const { activePlayerId, setActivePlayer } = useVideoPlayer();

    // Stop this player if another one becomes active
    useEffect(() => {
        if (activePlayerId !== playerId && isPlaying) {
            setIsPlaying(false);
            setShowPreview(true);
            if (playerRef.current) {
                playerRef.current.seekTo(0); // Reset to start
            }
            handleExitFullscreen();
        }
    }, [activePlayerId, isPlaying, playerId]);

    const handleShare = async () => {
        if (navigator.share && videoUrl) {
            try {
                navigator.share({
                    text: `${title}\n ${videoUrl}`,
                });
            } catch (error) {
                console.error('Error:', error);
            }
        } else {
            alert('Web Share API is not supported');
        }
    };

    const handlePlay = () => {
        playerId && setActivePlayer(playerId);
        setIsPlaying(true);
        setShowPreview(false);

        const container = containerRef.current as unknown as HTMLElement &
            CustomHTMLVideoElement;

        if (container) {
            const requestFullscreen =
                container.requestFullscreen ||
                container.webkitRequestFullscreen ||
                container.msRequestFullscreen;

            if (requestFullscreen) {
                requestFullscreen.call(container).catch((err) => {
                    console.error('Failed to enter fullscreen:', err);
                });
            }
        }
    };

    const handleExitFullscreen = async () => {
        if (document.fullscreenElement === containerRef.current) {
            try {
                await document.exitFullscreen();
            } catch (err) {
                console.warn('Failed to exit fullscreen:', err);
            }
        }
    };

    const handleVideoEnd = () => {
        setIsPlaying(false);
        setShowPreview(true);
        setActivePlayer(null);
        handleExitFullscreen();
    };

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement && isPlaying) {
                setIsPlaying(false);
                setShowPreview(true);
                setActivePlayer(null);
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener(
            'webkitfullscreenchange',
            handleFullscreenChange
        );
        document.addEventListener('msfullscreenchange', handleFullscreenChange); // IE/Edge

        return () => {
            document.removeEventListener(
                'fullscreenchange',
                handleFullscreenChange
            );
            document.removeEventListener(
                'webkitfullscreenchange',
                handleFullscreenChange
            );
            document.removeEventListener(
                'msfullscreenchange',
                handleFullscreenChange
            );
        };
    }, [isPlaying, setActivePlayer]);

    useEffect(() => {
        const containerElement = containerRef.current; // Store current ref in a variable

        return () => {
            if (
                document.fullscreenElement === containerElement &&
                typeof document.exitFullscreen === 'function'
            ) {
                document.exitFullscreen().catch((err) => {
                    console.warn('Cleanup fullscreen exit failed:', err);
                });
            }
        };
    }, []);

    return (
        <div ref={containerRef} className={styles.fullscreenPlayer}>
            {/* Share Button */}
            <button
                className={styles.shareButton}
                onClick={handleShare}
                title="Share video"
            >
                <svg
                    className={styles.shareButton__svg}
                    width="20"
                    height="11"
                    viewBox="0 0 20 11"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M15.954 5.67761C15.1164 4.26269 13.8371 3.16221 12.3129 2.54554C10.7887 1.92886 9.10404 1.83016 7.51824 2.26463C5.93244 2.69909 4.53336 3.64264 3.53629 4.95008C2.53923 6.25751 1.99944 7.85637 2 9.50061C2 9.76583 1.89464 10.0202 1.70711 10.2077C1.51957 10.3953 1.26522 10.5006 1 10.5006C0.734784 10.5006 0.48043 10.3953 0.292893 10.2077C0.105357 10.0202 1.96805e-08 9.76583 1.96805e-08 9.50061C-0.000129403 7.48934 0.638073 5.52993 1.8227 3.90455C3.00732 2.27917 4.67724 1.0717 6.59196 0.456035C8.50668 -0.159633 10.5674 -0.151729 12.4773 0.47861C14.3873 1.10895 16.0479 2.32919 17.22 3.96361L17.757 0.918611C17.7798 0.789259 17.8278 0.665661 17.8984 0.554875C17.9689 0.444088 18.0606 0.348283 18.1682 0.272928C18.2758 0.197574 18.3971 0.144145 18.5254 0.115695C18.6536 0.0872437 18.7861 0.0843272 18.9155 0.107112C19.0449 0.129896 19.1684 0.177935 19.2792 0.248486C19.39 0.319036 19.4858 0.410718 19.5612 0.518294C19.6365 0.625871 19.69 0.747236 19.7184 0.875461C19.7469 1.00369 19.7498 1.13626 19.727 1.26561L18.685 7.17461C18.6386 7.43574 18.4904 7.66775 18.273 7.81961C18.1313 7.91463 17.9695 7.97538 17.8002 7.99708C17.631 8.01877 17.4591 8.00081 17.298 7.94461L11.618 6.94361C11.3568 6.8976 11.1245 6.74969 10.9723 6.53243C10.8201 6.31517 10.7605 6.04635 10.8065 5.78511C10.8525 5.52387 11.0004 5.29161 11.2177 5.13943C11.4349 4.98724 11.7038 4.9276 11.965 4.97361L15.954 5.67761Z"
                        fill="white"
                    />
                </svg>
                Share
            </button>

            <style>
                {`
                    video::-webkit-media-controls-fullscreen-button {
                        display: none !important;
                    }
                    video::-webkit-media-controls {
                        overflow: hidden;
                    }
                `}
            </style>
            <ReactPlayer
                key={playerId}
                ref={playerRef}
                url={videoUrl}
                light={
                    showPreview && imageUrl ? (
                        <img
                            onLoad={handleImageLoad}
                            src={imageUrl}
                            alt="Thumbnail"
                        />
                    ) : (
                        false
                    )
                }
                playing={isPlaying && activePlayerId === playerId}
                controls={true}
                width="100%"
                height="100%"
                onPlay={handlePlay}
                onClickPreview={handlePlay}
                onEnded={handleVideoEnd}
                playIcon={
                    isImgLoaded ? (
                        <div className={styles.icon}>
                            <PlayIconRed />
                        </div>
                    ) : (
                        <></>
                    )
                }
                config={{
                    file: {
                        attributes: {
                            controlsList: 'nofullscreen',
                            disablePictureInPicture: false,
                        },
                    },
                }}
            />
        </div>
    );
};

export default FullscreenPlayer;
